import {
  type FileMetaData,
  type ParquetParsers,
  type SchemaTree,
} from "hyparquet";
import { default as partitionBy } from "lodash/partition";
import shuffle from "lodash/shuffle";

import { RepresentationRecord } from "@/shared/domain/topics";
import { CacheReadError, isAbortError } from "@/shared/errors";
import { type Readable, LocalFile } from "@/shared/fs";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { LoggerService } from "@/shared/services/LoggerService";

import { MessageFilter } from "../MessageFilter";
import { TopicReaderEventTarget } from "../TopicReader";

import { matchesFilter } from "./filter";
import { ParquetReader } from "./ParquetReader";
import {
  cachedPartSchema,
  cachedPartValues,
  ParquetWriter,
} from "./ParquetWriter";
import type {
  ColumnData,
  IterColumnChunksOptions,
  IterRowGroupsOptions,
  ReaderLike,
  RowGroup,
} from "./ReaderLike";
import { findColumnSubtree } from "./schema";

interface RowIndexRange {
  rowStart: number; // inclusive
  rowEnd: number; // exclusive
}

interface DatasetPartition {
  range: RowIndexRange;
  path: LocalFile;
}

/**
 * The source file's footer metadata, or a function that fetches it. The
 * function form defers the footer read until it is needed: creating the
 * manifest for a column not cached before, or downloading a part.
 */
type SourceMetadata = FileMetaData | (() => Promise<FileMetaData>);

interface FactoryArgs {
  /**
   * Whether parts outside the requested rows are downloaded in the background
   * after the requested ones. Defaults to true. A consumer that reads a
   * dataset once and disposes it turns this off, since it would dispose the
   * dataset before those downloads could complete.
   */
  backgroundPrefetch?: boolean;
  column: string;
  /** Replaces the parsers cached parts are read with, by name. */
  parsers?: Partial<ParquetParsers>;
  path: string[];
  signal?: AbortSignal;
  source: Readable; // source file
  sourceMetadata: SourceMetadata;
}

interface ConstructorArgs {
  backgroundPrefetch?: boolean;
  column: string;
  parsers?: Partial<ParquetParsers>;
  path: string[];
  partitions: DatasetPartition[];
  source: Readable;
  sourceMetadata: SourceMetadata;
}

/**
 * The parsers source rows are read with before a part is written. Each
 * returns a value in a form hyparquet-writer writes back unchanged (a
 * VARIANT's encoded fields, the bytes of a JSON, GEOMETRY or GEOGRAPHY
 * value, and a DECIMAL's unscaled integer), so a part stores the same values
 * as the source file.
 */
const PART_SOURCE_PARSERS: Partial<ParquetParsers> = {
  variantFromParts: (parts: Record<string, unknown>) => parts,
  decimalFromUnscaled: (unscaled: bigint) => unscaled,
  jsonFromBytes: (bytes: Uint8Array) => bytes,
  geometryFromBytes: (bytes: Uint8Array) => bytes,
  geographyFromBytes: (bytes: Uint8Array) => bytes,
};

/**
 * A transparent facade over a collection of Parquet file partitions.
 * Partitioning is done by row index.
 * A ParquetDataset has a manifest file that indexes its partitions and the row ranges they contain.
 *
 * Similar in spirit to an Apache Arrow "tabular dataset" (https://arrow.apache.org/docs/python/dataset.html)
 * and Hive-partitioned Parquet files (https://duckdb.org/docs/stable/data/partitioning/hive_partitioning.html).
 */
// prettier-ignore
export class ParquetDataset extends TopicReaderEventTarget implements ReaderLike {
  /**
   * Generate the cache path for a column.
   * Cache is keyed by: [topicId, representationId, columnName, associationId]
   */
  static cachePath(
    topicId: string,
    representation: RepresentationRecord,
    column: string,
  ): string[] {
    return [
      topicId,
      representation.representation_id,
      column,
      representation.association.association_id,
    ];
  }

  static async for(args: FactoryArgs): Promise<ParquetDataset> {
    const {
      backgroundPrefetch,
      column,
      parsers,
      path,
      signal,
      source,
      sourceMetadata,
    } = args;
    const manifest = new LocalFile({
      name: ParquetDataset.MANIFEST_FILE_NAME,
      parents: path,
    });

    // if the manifest file already exists, load from disk
    if (await manifest.exists()) {
      const contents = await manifest.read({ signal });
      const decoded = new TextDecoder().decode(contents);
      const partitions = JSON.parse(
        decoded,
        (key: string, value: JsonValue) => {
          if (key === "path") {
            return LocalFile.fromJSON(value);
          }

          return value;
        },
      ) as DatasetPartition[];
      return new ParquetDataset({
        backgroundPrefetch,
        column,
        parsers,
        partitions,
        path,
        source,
        sourceMetadata,
      });
    }

    // else create one
    const metadata =
      typeof sourceMetadata === "function"
        ? await sourceMetadata()
        : sourceMetadata;
    signal?.throwIfAborted();
    const partitions: DatasetPartition[] = [];
    let rowStart = 0;
    for (const [idx, rowGroup] of metadata.row_groups.entries()) {
      const rowCount = Number(rowGroup.num_rows);
      const rowEnd = rowStart + rowCount;

      const part = new LocalFile({
        name: `part_${idx}.parquet`,
        parents: path,
        trackLastAccessTime: false,
      });
      partitions.push({
        range: { rowStart: rowStart, rowEnd: rowEnd },
        path: part,
      });

      rowStart += rowCount;
    }

    // and save it to disk
    const content = new TextEncoder().encode(JSON.stringify(partitions));
    await manifest.write(content, { signal });

    return new ParquetDataset({
      backgroundPrefetch,
      column,
      parsers,
      partitions,
      path,
      source,
      sourceMetadata,
    });
  }

  private static MANIFEST_FILE_NAME = "manifest.json";

  private readonly backgroundPrefetch: boolean;
  public readonly column: string;
  private disposed: boolean = false;
  private downloads: Map<string, AbortController>;
  private readonly loadSourceMetadata: () => Promise<FileMetaData>;
  private readonly parsers?: Partial<ParquetParsers>;
  private partitions: DatasetPartition[];
  private path: string[];
  private source: Readable;
  private timeoutId?: ReturnType<typeof setTimeout>;

  constructor({
    backgroundPrefetch = true,
    column,
    parsers,
    partitions,
    path,
    source,
    sourceMetadata,
  }: ConstructorArgs) {
    super();
    this.backgroundPrefetch = backgroundPrefetch;
    this.column = column;
    this.downloads = new Map();
    this.parsers = parsers;
    this.partitions = partitions;
    this.path = path;
    this.source = source;
    this.loadSourceMetadata =
      typeof sourceMetadata === "function"
        ? sourceMetadata
        : () => Promise.resolve(sourceMetadata);
  }

  public get columns(): string[] {
    return [this.column];
  }

  /**
   * The number of row-index parts the source file is split into: one per
   * row group of the source file.
   */
  public get partCount(): number {
    return this.partitions.length;
  }

  public get rowCount(): bigint {
    const count = this.partitions.reduce(
      (accum, { range: { rowStart, rowEnd } }) => {
        const rowCount = rowEnd - rowStart;
        return accum + rowCount;
      },
      0,
    );
    return BigInt(count);
  }

  /**
   * The column's schema subtree as the first cached part stores it, or
   * undefined when no part is cached yet or that part cannot be read or
   * lacks the column. A cached part's schema maps to the same Arrow types
   * as the source file's, so a reader can learn the column's shape without
   * fetching the source footer.
   */
  public async cachedColumnSchema(): Promise<SchemaTree | undefined> {
    for (const partition of this.partitions) {
      if (!(await partition.path.exists())) {
        continue;
      }
      try {
        const reader = await ParquetReader.forFile({ file: partition.path });
        return (
          findColumnSubtree(reader.metadata.schema, this.column) ?? undefined
        );
      } catch {
        // The unreadable part stays cached until a read of its rows fails
        // and `readPartition` deletes it. A caller given undefined reads the
        // column's schema from the source file's footer.
        return undefined;
      }
    }
    return undefined;
  }

  dispose() {
    this.disposed = true;
    clearTimeout(this.timeoutId);
    this.abortActiveOperations();
    this.removeAllListeners();
  }

  async *iterColumnChunks(
    opts?: Partial<IterColumnChunksOptions>,
  ): AsyncGenerator<ColumnData, void, void> {
    const { rowIndexFilter, signal } = opts ?? {};

    await this.download({ rowIndexFilter, signal });
    signal?.throwIfAborted();

    for (const partition of this.partitions) {
      signal?.throwIfAborted();
      if (
        rowIndexFilter === undefined ||
        matchesFilter(partition.range, rowIndexFilter)
      ) {
        for await (const chunk of this.readPartition(
          partition,
          (reader) => reader.iterColumnChunks(opts),
          signal,
        )) {
          yield {
            ...chunk,
            columnName: this.column,
            rowStart: chunk.rowStart + partition.range.rowStart,
            rowEnd: chunk.rowEnd + partition.range.rowStart,
          };
        }
      }
    }
  }

  /**
   * Row groups are split across partitions.
   * The partitions themselves map to the source (remote) file's original row groups.
   */
  async *iterRowGroups(
    opts?: Partial<IterRowGroupsOptions>,
  ): AsyncIterable<RowGroup> {
    const { rowIndexFilter, signal } = opts ?? {};

    await this.download({
      rowIndexFilter,
      signal,
    });
    signal?.throwIfAborted();

    for (const partition of this.partitions) {
      signal?.throwIfAborted();
      if (
        rowIndexFilter === undefined ||
        matchesFilter(partition.range, rowIndexFilter)
      ) {
        for await (const rowGroup of this.readPartition(
          partition,
          (reader) => reader.iterRowGroups(opts),
          signal,
        )) {
          yield {
            ...rowGroup,
            rowStart: rowGroup.rowStart + partition.range.rowStart,
            rowEnd: rowGroup.rowEnd + partition.range.rowStart,
          };
        }
      }
    }
  }

  /**
   * Download the parts holding the rows `rowIndexFilter` matches (every part
   * when no filter is given) without reading them, so that several datasets
   * can fetch concurrently before any is iterated. Resolves once the matching
   * parts are cached.
   */
  public prefetch(
    opts?: Partial<{
      rowIndexFilter: MessageFilter<number>;
      signal: AbortSignal;
    }>,
  ): Promise<void> {
    return this.download(opts);
  }

  private abortActiveOperations() {
    for (const [id, controller] of this.downloads.entries()) {
      controller.abort();
      this.downloads.delete(id);
    }
  }

  private async download(
    opts?: Partial<{
      concurrency: number;
      rowIndexFilter: MessageFilter<number>;
      signal: AbortSignal;
    }>,
  ): Promise<void> {
    const { rowIndexFilter, signal } = opts ?? {};

    const uncached = await this.findUncachedPartitions();
    if (uncached.length === 0) {
      return;
    }

    const remoteReader = await ParquetReader.forFile({
      columns: this.columns,
      file: this.source,
      metadata: await this.loadSourceMetadata(),
      parsers: PART_SOURCE_PARSERS,
    });
    signal?.throwIfAborted();

    const [matching, nonmatching] = partitionBy(uncached, (part) => {
      if (rowIndexFilter === undefined) {
        // If no rowIndexFilter is defined, assume all partitions are needed upfront.
        // Happens when a panel needs all of a column's rows before it can use any (e.g., the plot panel).
        return true;
      }

      return matchesFilter(part.range, rowIndexFilter);
    });

    if (matching.length) {
      // stop the world: the UI doesn't have the data it urgently needs.
      this.abortActiveOperations();
    }

    if (matching.length) {
      const downloadId = Symbol(`ForegroundDataDownload_${this.source.id()}`);
      this.dispatchEvent(
        new CustomEvent("downloadstart", {
          detail: {
            id: downloadId,
            priority: "foreground",
          },
        }),
      );
      // Load partitions matching the current rowIndexFilter in the "foreground"...
      await Promise.all(
        // By shuffling the parts requiring download,
        // multiple panels that visualize the same column will more cooperatively multitask
        // the work of downloading the bytes.
        shuffle(matching).map((partition) => {
          return this.downloadPart(remoteReader, partition, signal);
        }),
      );
      this.dispatchEvent(
        new CustomEvent("downloadend", { detail: { id: downloadId } }),
      );
    }

    // ...and the others in the "background"
    if (this.backgroundPrefetch && nonmatching.length > 0) {
      const downloadId = Symbol(`BackgroundDataDownload_${this.source.id()}`);
      this.dispatchEvent(
        new CustomEvent("downloadstart", {
          detail: {
            id: downloadId,
            priority: "background",
          },
        }),
      );
      this.downloadInBackground(nonmatching, remoteReader, {
        downloadId
      });
    }
  }

  private downloadInBackground(
    partitions: DatasetPartition[],
    remoteReader: ParquetReader,
    opts: {
      batchSize?: number;
      downloadId: symbol;
    },
  ) {
    const { batchSize = 3, downloadId } = opts;

    if (partitions.length === 0) {
      this.timeoutId = undefined;
      this.dispatchEvent(
        new CustomEvent("downloadend", { detail: { id: downloadId } }),
      );
      return;
    }

    this.timeoutId = setTimeout(() => {
      if (this.disposed) {
        return;
      }

      // GM (2025-06)
      // By shuffling the parts requiring download,
      // multiple panels that visualize the same signal will more cooperatively multitask
      // the work of downloading the bytes.
      const remaining = shuffle(partitions);
      const batch = remaining.slice(0, batchSize);
      void Promise.all(
        batch.map((partition) => {
          return this.downloadPart(remoteReader, partition);
        }),
      )
        .then(() => {
          // Errors are caught and swallowed below.
          return this.downloadInBackground(
            remaining.slice(batchSize),
            remoteReader,
            opts,
          );
        })
        .catch((err) => {
          this.dispatchEvent(
            new CustomEvent("downloadend", { detail: { id: downloadId } }),
          );

          if (isAbortError(err)) {
            return;
          }

          // Report errors, but don't display to user:
          // If a backgrounded download fails, it will be retried when/if it is needed
          // when it becomes part of either a new batch of backgrounded downloads or a foregrounded download.
          ErrorMonitoringService.captureError(err);
        });
    }, 0);
  }

  private downloadPart(
    remoteReader: ParquetReader,
    partition: DatasetPartition,
    parentSignal?: AbortSignal,
  ) {
    const controller = new AbortController();

    const path = this.path.join("#");
    const id = `${path}#${partition.range.rowStart}#${partition.range.rowEnd}`;
    const lockKey = `ParquetDataset#downloadPart#${id}`;

    this.downloads.set(id, controller);

    // Each partition download has its own AbortController so that it's individually cancelable.
    // Additionally, foregrounded downloads are part of user-blocking UI operations,
    // so those downloads should be canceled if the UI operation is aborted.
    // Expect parentSignal to be defined when downloading in the foreground.
    const signal = parentSignal !== undefined
      ? AbortSignal.any([parentSignal, controller.signal])
      : controller.signal;

    return navigator.locks.request(lockKey, { signal }, async () => {
      signal.throwIfAborted();

      // now that lock on this part is acquired, double check that the part still requires download
      const exists = await partition.path.exists();
      signal.throwIfAborted();
      if (exists) {
        return;
      }

      LoggerService.log(
        "Downloading Parquet part",
        JSON.stringify(partition.range),
        "for path",
        path,
        "from worker",
        self.name,
      );

      let toPartValues:
        | ((values: ColumnData["columnData"]) => ColumnData["columnData"])
        | undefined;
      const makeWriter = async () => {
        let handle;
        try {
          handle = await partition.path.getWriteableSyncFileHandle(signal);
          signal.throwIfAborted();

          const columnTree = findColumnSubtree(
            remoteReader.metadata.schema,
            this.column,
          );

          if (columnTree === null) {
            throw new Error(
              `Failed to read data topic for column ${this.column}`,
              {
                cause:
                  "The remote Parquet file's metadata does not have an entry for this column.",
              },
            );
          }

          const schema = cachedPartSchema(columnTree);
          toPartValues = cachedPartValues(columnTree);

          const writer = ParquetWriter.create(handle, schema, {
            offset: partition.range.rowStart,
            // GM (2025-06):
            // keep row groups small to optimize for fine-grained reads
            // this is just slightly larger than the largest batch of values read from any panel (~8k from plot panel)
            rowGroupSize: 10_000,
          });
          return writer;
        } catch (err) {
          handle?.close();
          throw err; // re-throw
        }
      };

      let writer;
      try {
        writer = await makeWriter();
        signal.throwIfAborted();
        for await (const chunk of remoteReader.iterColumnChunks({
          rowGroupFilter: (rowGroup) => {
            return (
              rowGroup.rowStart === partition.range.rowStart &&
              rowGroup.rowEnd === partition.range.rowEnd
            );
          },
          signal,
        })) {
          signal.throwIfAborted();
          writer.queueWrite(
            toPartValues === undefined
              ? chunk.columnData
              : toPartValues(chunk.columnData),
            chunk.rowStart,
            chunk.rowEnd,
          );
        }
      } catch (err) {
        const exc = err instanceof Error ? err : new Error(String(err));

        try {
          writer?.cancel();
        } catch (reason) {
          const cancelExc =
            reason instanceof Error ? reason : new Error(String(reason));
          cancelExc.cause = exc;
          throw cancelExc;
        }

        // Assume any exception downloading the data makes the file unusable
        try {
          await partition.path.delete();
        } catch (reason) {
          const deleteExc =
            reason instanceof Error ? reason : new Error(String(reason));
          deleteExc.cause = exc;
          throw deleteExc;
        }

        throw exc; // re-throw
      } finally {
        writer?.close();
      }
    }).finally(() => {
      this.downloads.delete(id);
    });
  }

  private findUncachedPartitions(
    partitions: DatasetPartition[] = this.partitions,
  ): Promise<DatasetPartition[]> {
    return Promise.all(
      partitions.map(
        async (
          partition,
        ): Promise<[partition: DatasetPartition, exists: boolean]> => {
          const exists = await partition.path.exists();
          return [partition, exists];
        },
      ),
    ).then((results) => {
      return results
        .filter(([_, exists]) => !exists)
        .map(([partition]) => partition);
    });
  }

  /**
   * Open the cached file backing `partition` and yield what `read` produces from it.
   *
   * `download` completes before the partition loop starts, so every byte read here
   * comes from OPFS. A failure therefore means this partition's cached copy is
   * unusable, and `findUncachedPartitions` won't catch it on a later read because it
   * only checks that the file exists. Delete the file so the next read downloads it
   * again, then throw a {@link CacheReadError} so the panel can tell the user the
   * cache is suspect rather than quoting the parser that failed on the bytes.
   *
   * A cancelled read says nothing about the cached bytes, so an abort is rethrown
   * as-is and the file is left alone. That case is recognized from `signal` as well
   * as from the error, because these reads run in a web worker and an abort forwarded
   * into a worker arrives as a bare string reason rather than a DOMException, which
   * `isAbortError` does not match (see `AbortSignalReceiver` in `@/shared/webworker`).
   */
  private async *readPartition<T>(
    partition: DatasetPartition,
    read: (reader: ParquetReader) => AsyncIterable<T>,
    signal?: AbortSignal,
  ): AsyncGenerator<T, void, undefined> {
    try {
      const reader = await ParquetReader.forFile({
        columns: this.columns,
        file: partition.path,
        offset: partition.range.rowStart,
        parsers: this.parsers,
      });
      yield* read(reader);
    } catch (err) {
      if (isAbortError(err) || signal?.aborted === true) {
        throw err;
      }

      // Eviction is best-effort. If it fails, the entry survives and the next read
      // fails the same way, which is no worse than not having tried, so report the
      // failure rather than throwing it in place of the read error that prompted it.
      try {
        await partition.path.delete();
      } catch (evictionErr) {
        ErrorMonitoringService.captureError(
          new Error(
            `Failed to evict cached data at ${partition.path.toString()}`,
            { cause: evictionErr },
          ),
        );
      }

      throw new CacheReadError(
        `Failed to read cached data for column ${this.column}`,
        { cause: err },
      );
    }
  }
}
